// @ts-nocheck
const ENTRY_MODAL_KEYS = {
  completed: 'entry_modal_completed',
  dismissedAt: 'entry_modal_dismissed_at',
  pendingCouponCode: 'entry_modal_pending_coupon_code',
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/* ============================================================================
   TELEFONE POR PAÍS
   ----------------------------------------------------------------------------
   O país vem do data-attribute data-entry-phone-country no <dialog> ("BR",
   "MX" ou "AR"). Se não existir, é inferido do data-hs-nacionalidade que o
   Liquid já calcula. Sem nenhum dos dois, cai em BR (comportamento antigo).

   dialCode      -> código do país, sem "+"
   nationalLength-> tamanhos válidos do número nacional (sem código do país)
   mobilePrefix  -> dígito extra exigido no formato internacional de celular
                    (Argentina usa "9": +54 9 11 2345-6789)
   twoDigitAreas -> códigos de área de 2 dígitos; os demais são tratados como 3
   ============================================================================ */

const PHONE_COUNTRY_CONFIG = {
  BR: { dialCode: '55', nationalLength: [10, 11], mobilePrefix: '' },
  MX: { dialCode: '52', nationalLength: [10], mobilePrefix: '', twoDigitAreas: ['55', '33', '81'] },
  AR: { dialCode: '54', nationalLength: [10], mobilePrefix: '9', twoDigitAreas: ['11'], stripMobile15: true },
};

function getPhoneCountry(dialog) {
  const explicit = (dialog?.dataset.entryPhoneCountry || '').trim().toUpperCase();
  if (PHONE_COUNTRY_CONFIG[explicit]) return explicit;

  const nacionalidade = (dialog?.dataset.hsNacionalidade || '').trim().toLowerCase();
  if (nacionalidade.includes('argent')) return 'AR';
  if (nacionalidade.includes('xico') || nacionalidade.includes('mexico')) return 'MX';
  if (nacionalidade.includes('brasil') || nacionalidade.includes('brazil')) return 'BR';

  return 'BR';
}

function getPhoneConfig(country) {
  return PHONE_COUNTRY_CONFIG[country] || PHONE_COUNTRY_CONFIG.BR;
}

function getAreaLength(digits, config) {
  const twoDigitAreas = config.twoDigitAreas || [];
  return twoDigitAreas.includes(digits.slice(0, 2)) ? 2 : 3;
}

/* Remove o "15" que o argentino digita depois do código de área
   (ex.: 011 15 2345-6789 -> 11 2345 6789). */
function stripMobile15(digits, config) {
  if (!config.stripMobile15) return digits;
  const areaLength = getAreaLength(digits, config);
  if (digits.slice(areaLength, areaLength + 2) === '15') {
    return digits.slice(0, areaLength) + digits.slice(areaLength + 2);
  }
  return digits;
}

function normalizePhoneDigits(value, config) {
  let digits = (value || '').replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return '';
  return stripMobile15(digits, config);
}

function isValidPhone(value, country) {
  const config = getPhoneConfig(country);
  return normalizePhoneDigits(value, config).length >= 10;
}

/* --- Máscaras de digitação (apenas cosméticas) --- */

function formatBrazilPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function formatMexicoPhone(value) {
  const digits = value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 10);
  if (!digits) return '';

  const config = PHONE_COUNTRY_CONFIG.MX;
  const areaLength = getAreaLength(digits, config);
  if (digits.length <= areaLength) return `(${digits}`;

  const area = digits.slice(0, areaLength);
  const rest = digits.slice(areaLength);
  const headLength = areaLength === 2 ? 4 : 3;

  if (rest.length <= headLength) return `(${area}) ${rest}`;
  return `(${area}) ${rest.slice(0, headLength)}-${rest.slice(headLength)}`;
}

function formatArgentinaPhone(value) {
  let digits = value.replace(/\D/g, '').replace(/^0+/, '');

  // Se o usuário colou o número já internacional (+54 9 11 ...), tira o
  // código do país e o 9 de celular para mascarar só a parte nacional.
  if (digits.startsWith('54') && digits.length >= 12) {
    digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith('9')) digits = digits.slice(1);
  }

  digits = digits.slice(0, 12);
  if (!digits) return '';

  const config = PHONE_COUNTRY_CONFIG.AR;
  const areaLength = getAreaLength(digits, config);
  if (digits.length <= areaLength) return `(${digits}`;

  const area = digits.slice(0, areaLength);
  let rest = digits.slice(areaLength);
  let mobile = '';

  if (rest.startsWith('15')) {
    mobile = '15';
    rest = rest.slice(2);
  }

  // Área de 2 dígitos -> 8 dígitos locais (4-4). Área de 3 -> 7 locais (3-4).
  const headLength = areaLength === 2 ? 4 : 3;

  const head = mobile ? `(${area}) ${mobile}` : `(${area})`;
  if (!rest) return head;
  if (rest.length <= headLength) return `${head} ${rest}`;
  return `${head} ${rest.slice(0, headLength)}-${rest.slice(headLength, headLength + 4)}`;
}

function formatPhoneMask(value, country) {
  if (country === 'AR') return formatArgentinaPhone(value);
  if (country === 'MX') return formatMexicoPhone(value);
  return formatBrazilPhone(value);
}

/* --- Normalização para E.164 (o que vai para HubSpot e Lead Bridge) --- */

function formatPhoneForHubSpot(value, country) {
  const config = getPhoneConfig(country);
  const dial = config.dialCode;
  const rawDigits = (value || '').replace(/\D/g, '').replace(/^0+/, '');
  if (!rawDigits) return '';

  // Já veio com o código do país.
  const minNational = Math.min(...config.nationalLength);
  if (rawDigits.startsWith(dial) && rawDigits.length >= dial.length + minNational) {
    return `+${rawDigits}`;
  }

  const digits = stripMobile15(rawDigits, config);

  if (config.nationalLength.includes(digits.length)) {
    return `+${dial}${config.mobilePrefix || ''}${digits}`;
  }

  return `+${digits}`;
}

function splitFullName(fullName) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstname: '', lastname: '' };
  if (parts.length === 1) return { firstname: parts[0], lastname: '' };
  return { firstname: parts[0], lastname: parts.slice(1).join(' ') };
}

function buildHubSpotContactField(name, value) {
  return { objectTypeId: '0-1', name, value };
}

function getHubspotUtkCookie() {
  const match = document.cookie.match(/(^|;\s*)hubspotutk=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : '';
}

function buildHubSpotFormContext(options = {}) {
  const ctx = {
    pageUri: options.pageUri ?? window.location.href,
    pageName: options.pageName ?? (document.title || window.location.pathname),
  };
  if (options.ipAddress) ctx.ipAddress = options.ipAddress;
  if (options.hutk) ctx.hutk = options.hutk;
  return ctx;
}

function hasDiscountApplied(cart) {
  const hasCartLevelDiscount = Array.isArray(cart?.cart_level_discount_applications)
    && cart.cart_level_discount_applications.length > 0;
  const hasItemLevelDiscount = Array.isArray(cart?.items)
    && cart.items.some((item) => Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0);
  return hasCartLevelDiscount || hasItemLevelDiscount;
}

function getEntryModalI18nStrings() {
  const el = document.getElementById('entry-modal-i18n');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent);
  } catch {
    return {};
  }
}

async function fetchCart(strings) {
  const response = await fetch('/cart.js', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(strings.errorCartFetch);
  return response.json();
}

async function applyDiscountCode(code, strings) {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: code }),
  });
  if (!response.ok) throw new Error(strings.errorCouponApply);
  const cart = await response.json();
  return { cart, applied: hasDiscountApplied(cart) };
}

/* ============================================================================
   INTEGRAÇÃO COM O LEAD BRIDGE
   ----------------------------------------------------------------------------
   Envia o lead para o Lead Bridge, que grava o contato na Shopify via Admin
   API. Falhas são silenciosas de propósito: o fluxo do HubSpot e do cupom
   nunca deve ser interrompido por causa desta chamada.

   A URL e a key da loja vêm de data-attributes no <dialog>, então a mesma
   versão deste arquivo funciona em BR, MX e AR sem edição.
   ============================================================================ */

function submitToLeadBridge(dialog, { email, firstname, lastname, phone, tag }) {
  try {
    const endpoint = (dialog.dataset.leadBridgeUrl || '').trim();
    const storeKey = (dialog.dataset.leadBridgeStore || '').trim();

    // Sem configuração no tema a integração fica inativa, sem quebrar nada.
    if (!endpoint || !storeKey) return;

    const payload = {
      storeKey,
      email,
      firstName: firstname || '',
      lastName: lastname || '',
      phone: phone || '',
      tags: [tag],
      website: '', // campo isca: mantenha sempre vazio
    };

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true, // conclui o envio mesmo se a página for fechada logo depois
    }).catch(() => {
      /* silencioso por design */
    });
  } catch (error) {
    /* silencioso por design */
  }
}

function parseHandleList(value) {
  return (value || '')
    .split(',')
    .map((handle) => handle.trim().toLowerCase())
    .filter(Boolean);
}

function isCurrentPageExcluded(dialog) {
  const excludedHandles = parseHandleList(dialog?.dataset.entryExcludedHandles);
  if (!excludedHandles.length) return false;

  const currentHandle = (dialog?.dataset.entryPageHandle || '').trim().toLowerCase();
  if (!currentHandle) return false;

  return excludedHandles.includes(currentHandle);
}

function isCurrentPageIncluded(dialog) {
  const includedHandles = parseHandleList(dialog?.dataset.entryIncludedHandles);
  if (!includedHandles.length) return true; // vazio = mostrar em todas as páginas permitidas

  const currentHandle = (dialog?.dataset.entryPageHandle || '').trim().toLowerCase();
  if (!currentHandle) return false;

  return includedHandles.includes(currentHandle);
}

function shouldShowEntryModal(dialog) {
  const params = new URLSearchParams(window.location.search);
  if (params.get('utm_source') === 'reclameaqui') return false;
  if (isCurrentPageExcluded(dialog)) return false;
  if (!isCurrentPageIncluded(dialog)) return false;

  const isLoggedIn = dialog?.dataset.customerLoggedIn === 'true';
  const ordersCount = Number(dialog?.dataset.customerOrdersCount || 0);

  if (isLoggedIn && ordersCount > 0) return false;
  if (localStorage.getItem(ENTRY_MODAL_KEYS.completed) === 'true') return false;

  const dismissedAt = localStorage.getItem(ENTRY_MODAL_KEYS.dismissedAt);
  if (dismissedAt && (Date.now() - Number(dismissedAt)) < ONE_DAY_MS) return false;

  return true;
}

const initEntryModal = (root = document) => {
  const dialog = root.querySelector('[data-entry-modal]');
  if (!dialog || dialog.dataset.entryInit === 'true') return;
  dialog.dataset.entryInit = 'true';
  const STR = getEntryModalI18nStrings();
  const statusEl = dialog.querySelector('[data-entry-status]');
  const couponCode = ((dialog.dataset.entryCouponCode || '').trim());

  const hsPortalId = (dialog.dataset.hsPortalId || '39596724').trim();
  const hsFormId = (dialog.dataset.hsFormId || '4e89b582-6e3f-4170-83e0-733983b236a0').trim();
  const hsPhoneField = (dialog.dataset.hsPhoneField || 'phone').trim();
  const hsNacionalidade = (dialog.dataset.hsNacionalidade || '').trim();

  const phoneCountry = getPhoneCountry(dialog);

  const delayMs = parseInt(dialog.dataset.entryDelay, 10) || 20000;

  const confirmationDialog = root.querySelector('[data-entry-confirmation-modal]') || document.querySelector('[data-entry-confirmation-modal]');
  const confirmationCouponCodeEl = confirmationDialog ? confirmationDialog.querySelector('[data-entry-coupon-display]') : null;
  const confirmationCopyBtn = confirmationDialog ? confirmationDialog.querySelector('[data-entry-copy-coupon]') : null;
  const copyButtonDefaultLabel = confirmationCopyBtn ? confirmationCopyBtn.textContent.trim() : '';

  const submitBtn = dialog.querySelector('.btn-submit');
  const submitLabelEl = dialog.querySelector('[data-entry-submit-label]');
  const submitLabelDefault = submitLabelEl ? submitLabelEl.textContent.trim() : '';

  const open = () => {
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', 'open');
  };

  const close = () => {
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  };

  const dismiss = () => {
    localStorage.setItem(ENTRY_MODAL_KEYS.dismissedAt, String(Date.now()));
    close();
  };

  const openConfirmation = () => {
    if (!confirmationDialog) return;
    if (typeof confirmationDialog.showModal === 'function') confirmationDialog.showModal();
    else confirmationDialog.setAttribute('open', 'open');
  };

  const closeConfirmation = () => {
    if (!confirmationDialog) return;
    if (typeof confirmationDialog.close === 'function') confirmationDialog.close();
    else confirmationDialog.removeAttribute('open');
  };

  const showConfirmation = () => {
    if (confirmationCouponCodeEl) {
      confirmationCouponCodeEl.textContent = (couponCode || '-').toUpperCase();
    }
    close();
    openConfirmation();
  };

  /* ==========================================================================
     REALCE AO TENTAR FECHAR POR FORA
     --------------------------------------------------------------------------
     Clique no backdrop e tecla ESC não fecham mais o pop-up nem gravam o
     dismissedAt. Em vez disso, o card recebe a classe de animação, chamando
     atenção para o formulário. A regra das 24 horas só passa a valer quando o
     visitante usa o "X" ou o link "Agora não, obrigado" (data-entry-close).
     ========================================================================== */

  const NUDGE_CLASS = 'entry-modal-new__inner--nudge';
  const nudgeTarget = dialog.querySelector('.entry-modal-new__inner') || dialog;

  const clearNudge = () => nudgeTarget.classList.remove(NUDGE_CLASS);
  nudgeTarget.addEventListener('animationend', clearNudge);
  nudgeTarget.addEventListener('animationcancel', clearNudge);

  const nudge = () => {
    // Remove + reflow permite re-disparar a animação em cliques consecutivos.
    nudgeTarget.classList.remove(NUDGE_CLASS);
    void nudgeTarget.offsetWidth;
    nudgeTarget.classList.add(NUDGE_CLASS);
  };

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) nudge();
  });

  // Dispara no ESC (e no gesto de fechar nativo). preventDefault mantém aberto.
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    nudge();
  });

  dialog.querySelectorAll('[data-entry-close]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      dismiss();
    });
  });

  if (confirmationDialog && confirmationDialog.dataset.entryConfirmationInit !== 'true') {
    confirmationDialog.dataset.entryConfirmationInit = 'true';
    confirmationDialog.addEventListener('click', (event) => {
      if (event.target === confirmationDialog) closeConfirmation();
    });

    confirmationDialog.querySelectorAll('[data-entry-confirmation-close]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        closeConfirmation();
      });
    });

    if (confirmationCopyBtn) {
      confirmationCopyBtn.addEventListener('click', async () => {
        const code = ((confirmationCouponCodeEl?.textContent || '').trim());
        if (!code || code === '-') return;

        const previousLabel = confirmationCopyBtn.textContent;
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(code);
          } else {
            const tempInput = document.createElement('textarea');
            tempInput.value = code;
            tempInput.style.position = 'fixed';
            tempInput.style.opacity = '0';
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
          }
          confirmationCopyBtn.textContent = STR.copyButtonSuccess;
          confirmationCopyBtn.disabled = true;
          setTimeout(() => {
            confirmationCopyBtn.textContent = copyButtonDefaultLabel || previousLabel;
            confirmationCopyBtn.disabled = false;
          }, 1500);
        } catch (error) {
          confirmationCopyBtn.textContent = previousLabel;
        }
      });
    }
  }

  const setSubmitLabel = (text) => {
    if (submitLabelEl) submitLabelEl.textContent = text;
  };

  const showStatus = (message, type) => {
    if (!statusEl) return;
    statusEl.hidden = false;
    statusEl.textContent = message;
    statusEl.className = `entry-modal-new__status entry-modal-new__status--${type}`;
  };

  const clearStatus = () => {
    if (!statusEl) return;
    statusEl.hidden = true;
    statusEl.textContent = '';
    statusEl.className = 'entry-modal-new__status';
  };

  const setSubmitting = (isSubmitting) => {
    if (!submitBtn) return;
    submitBtn.disabled = isSubmitting;
    setSubmitLabel(isSubmitting ? STR.submitLoading : submitLabelDefault);
  };

  const setCompletedState = (buttonLabel) => {
    dialog.querySelectorAll('input, select').forEach((field) => { field.disabled = true; });
    if (submitBtn) {
      submitBtn.disabled = true;
      setSubmitLabel(buttonLabel || STR.statusRegistrationComplete);
    }
  };

  const phoneInput = dialog.querySelector('input[name="entry_phone"]');
  if (phoneInput) {
    phoneInput.addEventListener('input', () => {
      phoneInput.value = formatPhoneMask(phoneInput.value, phoneCountry);
    });
  }

  if (submitBtn) {
    submitBtn.addEventListener('click', async (event) => {
      event.preventDefault();
      clearStatus();
      let keepCompletedState = false;

      const nome = ((dialog.querySelector('input[name="entry_name"]') || {}).value || '').trim();
      const email = ((dialog.querySelector('input[name="entry_email"]') || {}).value || '').trim();
      const phone = ((dialog.querySelector('input[name="entry_phone"]') || {}).value || '').trim();

      if (!nome || !email || !phone) {
        showStatus(STR.validationRequiredFields, 'error');
        return;
      }
      if (nome.length < 3) {
        showStatus(STR.validationFullName, 'error');
        return;
      }
      if (!isValidEmail(email)) {
        showStatus(STR.validationEmail, 'error');
        return;
      }
      if (!isValidPhone(phone, phoneCountry)) {
        showStatus(STR.validationPhone, 'error');
        return;
      }

      var params = new URLSearchParams(window.location.search);

      try {
        setSubmitting(true);
        let userIp = '';
        try {
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          userIp = ipData.ip;
        } catch (e) {}

        // E.164 com "+" -> vai para a Shopify via Lead Bridge (a Admin API exige o "+").
        const phoneE164 = formatPhoneForHubSpot(phone, phoneCountry);
        if (!phoneE164) {
          showStatus(STR.validationPhone, 'error');
          return;
        }

        // Somente dígitos, com código do país -> vai para o HubSpot.
        const phoneForHubSpot = phoneE164.replace(/\D/g, '');

        const { firstname, lastname } = splitFullName(nome);
        const hsFields = [
          buildHubSpotContactField('firstname', firstname || nome),
          buildHubSpotContactField('email', email),
          buildHubSpotContactField(hsPhoneField, phoneForHubSpot),
          buildHubSpotContactField('origem_de_cadastro', 'popup_principal'),
          buildHubSpotContactField('origem_de_cadastro__crm_', 'popup_principal'),
          buildHubSpotContactField('utm_source', params.get('utm_source') || ''),
          buildHubSpotContactField('utm_medium', params.get('utm_medium') || ''),
          buildHubSpotContactField('utm_campaign', params.get('utm_campaign') || ''),
          buildHubSpotContactField('utm_term', params.get('utm_term') || ''),
          buildHubSpotContactField('utm_content', params.get('utm_content') || ''),
        ];
        if (hsNacionalidade) {
          hsFields.push(buildHubSpotContactField('nacionalidade', hsNacionalidade));
        }
        if (lastname) hsFields.push(buildHubSpotContactField('lastname', lastname));

        const hsData = {
          fields: hsFields,
          context: buildHubSpotFormContext({
            hutk: getHubspotUtkCookie(),
          }),
        };

        const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${hsPortalId}/${hsFormId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(hsData),
        });

        if (!response.ok) {
          let hubspotMessage = STR.errorFinalizeRegistration;
          try {
            const errorBody = await response.json();
            if (errorBody?.message) hubspotMessage = errorBody.message;
            else if (Array.isArray(errorBody?.errors) && errorBody.errors[0]?.message) {
              hubspotMessage = errorBody.errors[0].message;
            }
          } catch (parseError) {}
          throw new Error(hubspotMessage);
        }

        submitToLeadBridge(dialog, {
          email,
          firstname,
          lastname,
          phone: phoneE164,
          tag: 'pop-up-entrada',
        });

        localStorage.setItem(ENTRY_MODAL_KEYS.completed, 'true');
        localStorage.removeItem(ENTRY_MODAL_KEYS.dismissedAt);

        if (!couponCode) {
          keepCompletedState = true;
          setCompletedState(STR.statusRegistrationComplete);
          showConfirmation();
          return;
        }

        let cart;
        try {
          cart = await fetchCart(STR);
        } catch (cartError) {
          localStorage.setItem(ENTRY_MODAL_KEYS.pendingCouponCode, couponCode);
          keepCompletedState = true;
          setCompletedState(STR.statusCouponLinked);
          showConfirmation();
          return;
        }

        if (!cart.item_count) {
          localStorage.setItem(ENTRY_MODAL_KEYS.pendingCouponCode, couponCode);
          keepCompletedState = true;
          setCompletedState(STR.statusCouponSaved);
          showConfirmation();
          return;
        }

        if (hasDiscountApplied(cart)) {
          localStorage.setItem(ENTRY_MODAL_KEYS.pendingCouponCode, couponCode);
          keepCompletedState = true;
          setCompletedState(STR.statusCouponSaved);
          showConfirmation();
          return;
        }

        const { applied } = await applyDiscountCode(couponCode, STR);
        if (!applied) {
          localStorage.setItem(ENTRY_MODAL_KEYS.pendingCouponCode, couponCode);
          keepCompletedState = true;
          setCompletedState(STR.statusCouponSaved);
          showConfirmation();
          return;
        }

        localStorage.removeItem(ENTRY_MODAL_KEYS.pendingCouponCode);
        window.dispatchEvent(new CustomEvent('entry-modal:coupon-applied', { detail: { code: couponCode } }));
        keepCompletedState = true;
        setCompletedState(STR.statusCouponApplied);
        showConfirmation();
      } catch (error) {
        showStatus(error.message || STR.errorGenericSubmit, 'error');
      } finally {
        if (!keepCompletedState) setSubmitting(false);
      }
    });
  }

  if (shouldShowEntryModal(dialog)) {
    setTimeout(() => open(), delayMs);
  }

  window.openEntryModal = open;
  window.closeEntryModal = close;
};

document.addEventListener('DOMContentLoaded', () => initEntryModal());